import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import React, { FC, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { updateExternalDeliveryDateTemplate } from 'apollo/templates/delivery-schedule';
import IntExtDeliveryDatePicker from 'components/orders/order-details/int-ext-delivery-date-picker.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Order } from 'models/order';
import { LabClient } from 'models/client-directory';
import { getDeliveryAdvanceWorkingDate, WorkingDayBeforeCounter } from 'utils/internal-delivery-advance-working-date';
import { ApolloCache, Reference } from '@apollo/client';
import { getLabClientByIdTemplate } from 'apollo/templates/client-directory/query.graphql';
import Loader from 'components/shared/loader/loader.component';
import { useSeedOnOpen } from 'hooks/useSeedOnOpen';

type ChangeExternalDeliveryDateDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order?: Order;
    isUpdateCache?: boolean;
};

const ChangeExternalDeliveryDateDialog: FC<ChangeExternalDeliveryDateDialogProps> = ({
    onUpdate,
    onClose,
    open,
    order,
    isUpdateCache,
}: ChangeExternalDeliveryDateDialogProps) => {
    const { t: translate } = useTranslation(['common', 'delivery-schedule', 'orders']);
    const [externalDeliveryDate, setExternalDeliveryDate] = useState<Date | null>(null);
    const [externalDeliveryDaysAdvance, setExternalDeliveryDaysAdvance] = useState<number>();
    const { showBoundary } = useDenthubErrorBoundary();

    const [childClientSettings, setChildClientSettings] = useState<LabClient>({} as LabClient);

    const { loading: labClientByIdLoading } = useQuery(getLabClientByIdTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            customerId: order?.deliveryDetails?.destination?._id,
        },
        onCompleted: ({ labClientById: childClientSettings }: { labClientById: LabClient }) => {
            setChildClientSettings(childClientSettings);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const updateCache = (cache: ApolloCache<Order>, order: Order) => {
        cache.modify({
            fields: {
                deliveryScheduleNoDeadlineOrders(
                    existingNoDeadlineOrderRefs: Reference | (Reference | undefined)[],
                    { readField },
                ) {
                    if (existingNoDeadlineOrderRefs instanceof Array) {
                        return [...existingNoDeadlineOrderRefs].filter((noDeadlineOrderRef: Reference | undefined) => {
                            return order._id !== readField('_id', noDeadlineOrderRef);
                        });
                    }
                    return [];
                },
            },
        });
    };

    useSeedOnOpen(
        open,
        () => {
            if (order) {
                if (order.deliveryDetails?.externalDeliveryDate) {
                    setExternalDeliveryDate(order.deliveryDetails.externalDeliveryDate);
                    const deliveryDate = new Date(order.deliveryDetails.date);
                    const workingDayCount = WorkingDayBeforeCounter(
                        deliveryDate,
                        order.deliveryDetails.externalDeliveryDate,
                    );
                    setExternalDeliveryDaysAdvance(workingDayCount);
                } else {
                    if (childClientSettings?.externalDeliveryDaysAdvance) {
                        const deliveryDate = new Date(order.deliveryDetails?.date);
                        const advanceDate = getDeliveryAdvanceWorkingDate(
                            deliveryDate,
                            childClientSettings.externalDeliveryDaysAdvance as number,
                        );
                        if (advanceDate) {
                            setExternalDeliveryDaysAdvance(childClientSettings.externalDeliveryDaysAdvance as number);
                            setExternalDeliveryDate(advanceDate);
                        }
                    }
                }
            }
        },
        [childClientSettings],
    );

    const [changeExternalDeliveryDate] = useMutation(updateExternalDeliveryDateTemplate, {
        onCompleted: ({ updateExternalDeliveryDate }) => {
            onClose();
            onUpdate?.(updateExternalDeliveryDate);
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<Order>, { data: { updateExternalDeliveryDate } }) {
            if (order && isUpdateCache) {
                const orderData = Object.assign({}, order);
                orderData.deliveryDetails = Object.assign({}, orderData.deliveryDetails, {
                    externalDeliveryDate: updateExternalDeliveryDate.deliveryDetails.internalDeliveryDate as Date,
                });
                updateCache(cache, orderData);
            }
        },
    });

    const handleChangeExternalDeliveryDate = async () => {
        await changeExternalDeliveryDate({
            variables: {
                orderId: order?._id,
                externalDeliveryDate: externalDeliveryDate,
            },
        });
    };

    const handleExternalDeliveryDateChange = (date: Date | null) => {
        setExternalDeliveryDate(date);
    };

    if (labClientByIdLoading) {
        return <Loader />;
    }

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={onClose}
            title={translate('delivery-schedule:CHANGE_EXTERNAL_DEADLINE')}
            actions={
                <Button disabled={!externalDeliveryDate} variant="contained" onClick={handleChangeExternalDeliveryDate}>
                    {translate('delivery-schedule:CHANGE_EXTERNAL_DEADLINE')}
                </Button>
            }
        >
            <Box>
                <IntExtDeliveryDatePicker
                    label={translate('orders:EXTERNAL_DELIVERY_DATE')}
                    selectedDate={externalDeliveryDate}
                    deliveryDaysAdvance={externalDeliveryDaysAdvance}
                    clinicDeliveryDate={order?.deliveryDetails?.date}
                    onChangeDate={handleExternalDeliveryDateChange}
                    isExternal={true}
                    dataTest="change-external-delivery-date-dialog.external-delivery-date-picker"
                />
            </Box>
        </DHDialog>
    );
};

export default ChangeExternalDeliveryDateDialog;
